import styled from 'styled-components';
import { useCallback } from 'react';
import useCreateMarketFromPositions from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarketFromPositions';
import {
  useSelectedPositionRowsActions,
  useSelectedPositionRowsView,
} from '@paradigm/features/src/positions/contexts/selected-positions';
import { PositionView } from '@paradigm/features/src/positions/entities/positions';
import { toast } from '#/components/UnifiedRfqs/toast';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import Button from '@paradigm/design-system/src/components/Button';
import { usePositionsView } from '@paradigm/features/src/positions/contexts/store';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { isProductCodeDisabledForType } from '@paradigm/features/src/products/domain';
import PillButton from '../components/PillButton';

export default function PositionsMarketCreation() {
  const { isEmptySelection } = useSelectedPositionRowsView();
  const { clearSelectedRows } = useSelectedPositionRowsActions();
  const selectedPositions = useSelectedPositions();
  const trackAction = useTrackAction();
  const handleClearSelection = useCallback(() => {
    trackAction('clear selection');
    clearSelectedRows();
  }, [clearSelectedRows, trackAction]);

  return (
    <Container isDisabled={isEmptySelection}>
      <CreateMarketButtons
        positions={selectedPositions}
        isDisabled={isEmptySelection}
        baseDDTag="Position Multi Select"
        isHeader
      />
      <Button
        ariaLabel="clear positions selection"
        variant="link"
        bold
        onClick={handleClearSelection}
        disabled={isEmptySelection}
        data-testid="positions-table-header-clear-button"
      >
        Clear
      </Button>
    </Container>
  );
}

interface CreateMarketButtonsProps {
  readonly positions: ReadonlyArray<PositionView>;
  readonly isDisabled?: boolean;
  readonly isHeader?: boolean;
  readonly baseDDTag: string;
}

export function CreateMarketButtons({
  positions,
  isDisabled = false,
  isHeader,
  baseDDTag,
}: CreateMarketButtonsProps) {
  const createMarket = useCreateMarketFromPositions({
    onError: toast,
    positions,
  });
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const isDisabledForAnyProduct = useCallback(
    (type: 'RFQ' | 'OB') =>
      positions.some((position) =>
        isProductCodeDisabledForType(position.productCode, type),
      ),
    [positions],
  );
  const trackAction = useTrackAction();

  const handleCreateRFQ = useCallback(() => {
    trackAction('create market', { auctionType: 'RFQ' });
    createMarket('RFQ');
  }, [createMarket, trackAction]);

  const handleCreateOB = useCallback(() => {
    trackAction('create market', { auctionType: 'OB' });
    createMarket('OB');
  }, [createMarket, trackAction]);

  return (
    <>
      <CreateButton
        ariaLabel="create rfq"
        onClick={handleCreateRFQ}
        disabled={
          isDisabled ||
          isPlatformCreationDisabled ||
          isDisabledForAnyProduct('RFQ')
        }
        data-dd-action-name={`${baseDDTag} Create RFQ`}
        data-testid={
          isHeader === true ? 'positions-table-header-rfq-button' : ''
        }
      >
        RFQ
      </CreateButton>
      <CreateButton
        ariaLabel="create ob"
        onClick={handleCreateOB}
        disabled={
          isDisabled ||
          isPlatformCreationDisabled ||
          isDisabledForAnyProduct('OB')
        }
        data-dd-action-name={`${baseDDTag} Create OB`}
        data-testid={
          isHeader === true ? 'positions-table-header-ob-button' : ''
        }
      >
        OB
      </CreateButton>
    </>
  );
}

function useSelectedPositions() {
  const { selectedRows } = useSelectedPositionRowsView();
  const { positions } = usePositionsView();
  const rowIndices = new Set(
    Object.keys(selectedRows).map((key) => Number(key)),
  );
  const selectedPositions = positions.filter((_, index) =>
    rowIndices.has(index),
  );
  return selectedPositions;
}

const Container = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  gap: 8px;
  border-radius: 12px;
  height: 22px;
  opacity: ${(props) => (props.isDisabled === true ? 0.3 : 1)};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 1rem;
  }
`;

const CreateButton = styled(PillButton)`
  padding: 0;
  border-radius: 16px;
  width: 44px;
`;
